import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Chip, Menu, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

/**
 * Small, explicit, code-level allowlist of the Project Documents tools
 * selectable through the chat composer's tool picker -- deliberately NOT a
 * dynamic/plugin-driven registry (same "small explicit mapping" convention
 * this codebase already uses for chat-schedule-tool-dispatch.js's own
 * REGISTRY). Each `id` is sent to the server verbatim as askQuestion's
 * `tool` field; the server independently validates it against its OWN
 * Project-Assistant-wide allowlist (services/ai/project-docs/
 * project-assistant-tools.js's own PROJECT_ASSISTANT_TOOLS) -- a concept
 * deliberately kept SEPARATE from chat-schedule-tool-intent.js's own
 * SUPPORTED_SCHEDULE_TOOL_TYPES (the closed set of durable schedule types
 * the natural-language matcher/dispatcher recognize) -- see
 * project-assistant-tools.js's own docstring for why a schedule type
 * existing/working elsewhere is never the same claim as "the picker offers
 * it". This browser-side list is a UX affordance only, never itself a
 * trust boundary. `label` also doubles as this frontend's single source of
 * truth for how to DISPLAY a turn whose typed composer text was blank (see
 * CHAT_TOOL_LABEL_BY_ID, used by ChatPanel.js's PendingTurnBubble/message
 * rendering) -- never duplicated/invented a second time elsewhere.
 *
 * These IDs are deliberately the SAME strings as the existing schedule
 * type / toolResult.tool identifiers (see ChatPanel.js's own
 * TOOL_RESULT_LABELS) rather than a separate "capability name" -- for a
 * schedule tool, the capability IS the schedule type, and introducing a
 * second name for the same concept would be pure translation-layer
 * overhead with no disambiguation benefit (there is nothing else today
 * named "piping").
 *
 * This picker's first slice intentionally ships with only the three tools
 * explicitly called out for it, so the picker foundation itself can be
 * reviewed before more capabilities are layered onto it. Adding a future
 * schedule-kind tool (e.g. Equipment, whenever it is intentionally decided
 * to expose it here) means adding one entry here AND one entry to
 * PROJECT_ASSISTANT_TOOLS server-side; a future non-schedule tool (e.g.
 * Compare Drawings) would use its own `kind` there instead, never
 * pretending to be a schedule type -- intentional, coordinated additions
 * on both sides, never an accidental one-sided exposure.
 */
export const CHAT_TOOL_OPTIONS = [
  { id: 'piping', label: 'Piping Schedule' },
  { id: 'ductwork', label: 'Ductwork Schedule' },
  { id: 'electrical_material', label: 'Electrical Material Schedule' },
];

export const CHAT_TOOL_LABEL_BY_ID = CHAT_TOOL_OPTIONS.reduce((acc, tool) => {
  acc[tool.id] = tool.label;
  return acc;
}, {});

/**
 * Composer control (ChatPanel.js), rendered as the message TextField's own
 * `startAdornment` -- INSIDE the same single bordered input as the text
 * area and the Send `endAdornment`, never a separate bordered control next
 * to it (that would read as a double border). Two visual states only:
 *   - nothing selected: a plain, borderless "+" icon button (matching the
 *     Send icon button's own borderless style); opens a small popover menu
 *     listing CHAT_TOOL_OPTIONS.
 *   - a tool selected: a compact Chip naming it. Clicking the chip body
 *     reopens the SAME menu (to switch tools); its delete ("x") action
 *     clears the selection back to plain chat -- never a separate control.
 *
 * Selecting a tool ONLY sets this structured state (the composer's
 * `selectedTool` prop, threaded by ProjectDocumentsAssistantPage.js into
 * askQuestion's `tool` field) -- it never itself calls askQuestion/executes
 * anything. Send is always what executes the turn (see
 * ProjectDocumentsAssistantPage.js's handleAskQuestion); selecting a tool
 * only enables Send even when the composer text is blank, since the
 * structured tool selection alone already fully specifies a valid request.
 */
const ChatToolPicker = ({ selectedTool, onSelect, onClear, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleSelect = (toolId) => {
    onSelect(toolId);
    handleClose();
  };

  return (
    <Box sx={{ flexShrink: 0 }}>
      {selectedTool ? (
        <Chip
          size="small"
          label={CHAT_TOOL_LABEL_BY_ID[selectedTool] || selectedTool}
          onClick={disabled ? undefined : handleOpen}
          onDelete={disabled ? undefined : onClear}
          disabled={disabled}
          sx={{ fontWeight: 600, height: 26 }}
        />
      ) : (
        <Tooltip title="Select a tool">
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={handleOpen}
              disabled={disabled}
              aria-label="Select a tool"
              aria-haspopup="menu"
              aria-expanded={open}
              // Tighter than IconButton's own small-size default (5px) --
              // this button sits INSIDE the composer's own bordered input
              // (its own startAdornment slot, see ChatPanel.js), not as a
              // standalone control with its own generous click-target
              // padding needs.
              sx={{ p: 0.25 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {CHAT_TOOL_OPTIONS.map((tool) => (
          <MenuItem
            key={tool.id}
            selected={tool.id === selectedTool}
            onClick={() => handleSelect(tool.id)}
          >
            {tool.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ChatToolPicker;
